// src/App.js
import React from "react";
import "./App.css";
import "./styles/portfolio.css";
import "./styles/cookie-consent.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
} from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ConsentProvider, useConsent } from "./contexts/ConsentContext";
import { DEFAULT_LANG, isSupportedLang } from "./constants/languages";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Works from "./pages/Works";
import WorkDetail from "./pages/WorkDetail";
import Bio from "./pages/Bio";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Contact from "./pages/Contact";
import FakePage from "./pages/fake"; // <-- case-safe
import TinderPage from "./pages/tinder"; // <-- case-safe
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";

// Splash
import SplashIntro from "./components/SplashIntro";

// Scroll-to-top su cambio rotta
import ScrollToTop from "./components/ScrollToTop";

// Tag hreflang
import SEO from "./components/SEO";
import AnalyticsManager from "./components/AnalyticsManager";
import CookieConsentBanner from "./components/CookieConsentBanner";
import CookiePreferencesDialog from "./components/CookiePreferencesDialog";

function ConsentAwareSplash() {
  const { hasDecision } = useConsent();
  if (!hasDecision) return null;
  return <SplashIntro image="/media/intro.jpg" storageKey="splash_seen" />;
}

// Home nuda "/": manda alla lingua salvata in precedenza, altrimenti a IT
function RootRedirect() {
  let saved = null;
  try {
    saved = localStorage.getItem("language");
  } catch {
    // localStorage non disponibile: ignora
  }
  const lang = isSupportedLang(saved) ? saved : DEFAULT_LANG;
  return <Navigate to={`/${lang}`} replace />;
}

// Guardia sul segmento :lang — se non è "it" o "en" torna alla home IT
function LangGate() {
  const { lang } = useParams();
  if (!isSupportedLang(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }
  return <Outlet />;
}

// Redirect permanente (a runtime) dai vecchi indirizzi senza prefisso lingua
function LegacyRedirect({ to }) {
  return <Navigate to={to} replace />;
}

// Redirect per i vecchi indirizzi con id (es. /works/12 -> /it/works/12)
function LegacyIdRedirect({ base }) {
  const { id } = useParams();
  return <Navigate to={`/${DEFAULT_LANG}/${base}/${id}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ConsentProvider>
          <ScrollToTop behavior="instant" />
          <SEO />
          <AnalyticsManager />
          <CookieConsentBanner />
          <CookiePreferencesDialog />
          <ConsentAwareSplash />

          <div className="App">
            <Header />
            <main className="page-offset">
              <Routes>
              {/* Home nuda -> lingua preferita (o IT) */}
              <Route path="/" element={<RootRedirect />} />

              {/* Rotte con prefisso lingua /it/... e /en/... */}
              <Route path="/:lang" element={<LangGate />}>
                <Route index element={<Home />} />
                <Route path="works" element={<Works />} />
                <Route path="works/:id" element={<WorkDetail />} />
                <Route path="bio" element={<Bio />} />
                <Route path="articles" element={<Articles />} />
                <Route path="articles/:id" element={<ArticleDetail />} />
                <Route path="contact" element={<Contact />} />
                <Route path="fake" element={<FakePage />} />
                <Route path="tinder" element={<TinderPage />} />
                <Route path="privacy" element={<PrivacyPolicy />} />
                <Route path="cookies" element={<CookiePolicy />} />
              </Route>

              {/* Redirect permanenti dai vecchi indirizzi senza prefisso */}
              <Route path="/works" element={<LegacyRedirect to={`/${DEFAULT_LANG}/works`} />} />
              <Route path="/works/:id" element={<LegacyIdRedirect base="works" />} />
              <Route path="/bio" element={<LegacyRedirect to={`/${DEFAULT_LANG}/bio`} />} />
              <Route path="/articles" element={<LegacyRedirect to={`/${DEFAULT_LANG}/articles`} />} />
              <Route path="/articles/:id" element={<LegacyIdRedirect base="articles" />} />
              <Route path="/contact" element={<LegacyRedirect to={`/${DEFAULT_LANG}/contact`} />} />
              <Route path="/fake" element={<LegacyRedirect to={`/${DEFAULT_LANG}/fake`} />} />
              <Route path="/tinder" element={<LegacyRedirect to={`/${DEFAULT_LANG}/tinder`} />} />
              <Route path="/privacy" element={<LegacyRedirect to={`/${DEFAULT_LANG}/privacy`} />} />
              <Route path="/cookies" element={<LegacyRedirect to={`/${DEFAULT_LANG}/cookies`} />} />

              {/* Qualsiasi altro indirizzo sconosciuto -> home IT */}
              <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </ConsentProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
